import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Eye, Clock, Calendar, Percent, Store, Lock } from "lucide-react";
import type { Deal } from "@/data/deals";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/utils/cartContext";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

interface DealCardProps {
  deal: Deal;
  isInCart?: boolean;
  onAddToCart?: (deal: Deal) => void;
}

export default function DealCard({ deal, isInCart = false, onAddToCart }: DealCardProps) {
  const navigate = useNavigate();
  const { addToCart, removeFromCart, isInCart: checkIsInCart } = useCart();
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();
  
  const actuallyInCart = checkIsInCart(deal.id); // Use string ID directly

  const handleViewDetails = () => {
    navigate(`/deal/${deal.id}`);
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (actuallyInCart) {
      removeFromCart(deal.id); // Use string ID directly
      toast({
        title: "Removed from cart",
        description: "Deal has been removed from your cart.",
      });
    } else {
      // Create cart item directly
      const cartItem = {
        id: deal.id, // Use string ID directly
        name: deal.title,
        location: deal.location,
        state: deal.location.split(',')[1]?.trim() || "Multiple",
        price: deal.discountedPrice * 100, // Convert to cents for cart system consistency
        impressions: deal.impressions,
        uniqueVisitors: deal.uniqueVisitors || 0,
        frequency: deal.frequency || 1,
        size: deal.size || "Various",
        type: "Deal" as const,
        status: deal.isAvailable ? "available" as const : "sold-out" as const,
        image: deal.imageUrl,
        isDeal: true,
        currencyCode: 'N/A', // Deals may not have individual currency data
        specifications: {
          resolution: deal.size || "Various",
          duration: deal.duration || "Various"
        },
        priceFormat: "day-based" as "day-based" // Set to either "spot-based" or "day-based" as required by CartItem type
      };
      addToCart(cartItem);
      toast({
        title: "Added to cart",
        description: "Deal has been added to your cart successfully.",
      });
    }
  };

  const handleVisitStore = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Set back URL context based on current page
    const currentPath = window.location.pathname;
    sessionStorage.setItem('mediaOwnerBackUrl', currentPath);
    navigate(`/store/${deal.mediaOwnerId}`);
  };

  const getInventoryTypeColor = (type: string) => {
    switch (type) {
      case 'Digital': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Classic': return 'bg-green-100 text-green-700 border-green-200';
      case 'Transit': return 'bg-orange-100 text-orange-700 border-orange-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <Card className="group hover:shadow-lg transition-all duration-300 cursor-pointer" onClick={handleViewDetails}>
      <CardContent className="p-0">
        <div className="relative">
          <img
            src={deal.imageUrl}
            alt={deal.title}
            className="w-full h-48 object-cover rounded-t-lg"
          />
          
          {/* Discount Badge */}
          <div className="absolute top-3 left-3">
            <Badge className="bg-red-500 text-white font-bold text-sm px-2 py-1">
              <Percent className="h-3 w-3 mr-1" />
              {deal.discountPercentage}% OFF
            </Badge>
          </div>

          {/* Inventory Type Badge */}
          <div className="absolute top-3 right-3">
            <Badge className={getInventoryTypeColor(deal.inventoryType)}>
              {deal.inventoryType}
            </Badge>
          </div>

          {/* Availability Status */}
          <div className="absolute bottom-3 right-3">
            <Badge className={deal.isAvailable ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
              {deal.isAvailable ? "Available" : "Sold Out"}
            </Badge>
          </div>
        </div>

        <div className="p-4 space-y-3">
          {/* Title and Description */}
          <div>
            <h3 className="font-bold text-lg text-gray-900 group-hover:text-emerald-600 transition-colors line-clamp-1">
              {deal.title}
            </h3>
            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
              {deal.description}
            </p>
          </div>

          {/* Media Owner */}
          <div className="flex items-center space-x-2 text-sm">
            <img 
              src={deal.mediaOwnerLogo} 
              alt={deal.mediaOwnerName}
              className="w-6 h-6 rounded-full object-cover"
            />
            <button 
              onClick={handleVisitStore}
              className="text-emerald-600 hover:text-emerald-700 font-medium hover:underline"
            >
              {deal.mediaOwnerName}
            </button>
          </div>

          {/* Location and Impressions */}
          {isAuthenticated ? (
            <div className="flex items-center justify-between text-sm text-gray-600">
              <div className="flex items-center space-x-1">
                <MapPin className="h-4 w-4" />
                <span>{deal.location}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Eye className="h-4 w-4" />
                <span>{deal.impressions?.toLocaleString()}</span>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center space-x-1 text-sm text-gray-600">
                <MapPin className="h-4 w-4" />
                <span>{deal.location}</span>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                <div className="flex items-center justify-center text-xs text-gray-600">
                  <Lock className="mr-2 h-3 w-3" />
                  <span>Login to view full audience and performance data</span>
                </div>
              </div>
            </div>
          )}

          {/* Deal Validity */}
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Calendar className="h-4 w-4" />
            <span>Valid: {new Date(deal.validFrom).toLocaleDateString()} - {new Date(deal.validTo).toLocaleDateString()}</span>
          </div>

          {/* Minimum Duration */}
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Clock className="h-4 w-4" />
            <span>Min Duration: {deal.minDuration}</span>
          </div>

          {/* Pricing */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <span className="text-lg font-bold text-emerald-600">
                  ${deal.discountedPrice?.toLocaleString()}
                </span>
                <span className="text-sm text-gray-500 line-through">
                  ${deal.originalPrice?.toLocaleString()}
                </span>
              </div>
              <p className="text-xs text-gray-500">Per {deal.duration}</p>
            </div>
          </div>

          {/* Tags */}
          {deal.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {deal.tags.slice(0, 3).map((tag, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
              {deal.tags.length > 3 && (
                <Badge variant="secondary" className="text-xs">
                  +{deal.tags.length - 3}
                </Badge>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-2 pt-2">
            <Button
              variant="outline"
              onClick={handleViewDetails}
              className="flex-1 border-emerald-200 text-emerald-600 hover:bg-emerald-50"
            >
              View Details
            </Button>
            <Button
              onClick={handleAddToCart}
              disabled={!deal.isAvailable}
              className={`flex-1 ${
                actuallyInCart 
                  ? "bg-red-600 hover:bg-red-700 text-white" 
                  : "bg-emerald-600 hover:bg-emerald-700 text-white"
              }`}
            >
              {!deal.isAvailable 
                ? "Sold Out" 
                : actuallyInCart 
                ? "Remove" 
                : "Add to Cart"
              }
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}